import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faThumbsUp,
  faComment,
  faPaperPlane,
  faGlobeAmericas,
  faCheckCircle,
  faArrowRight,
  faCalendarAlt,
  faEye,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faWhatsapp,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import ApiServices from "../Services/ApiServices";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { storageHelper } from "../../Helpers/storageHelper";
import BlogComment from "./BlogComment";
import htmlHelper from "../../Helpers/htmlHelper";
import MiniLoader from "../Shared/MiniLoader";
import Header from "../Shared/Header";
import platformLogo from "../../assets/images/new_log_design.svg";

const BlogViewer = () => {
  const navigate = useNavigate();
  const { postId } = useParams();

  const shareMenuRef = useRef(null);

  const [post, setPost] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComments, setShowComments] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  // Fetch post from the dedicated blog endpoint
  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;

      try {
        setLoading(true);
        const response = await ApiServices.get(`Webstie/Blog/${postId}`);

        if (response && response.check && response.data) {
          const fetchedPost = response.data;
          setPost(fetchedPost);
          setRelatedBlogs(fetchedPost.related_blogs || []);
          setLikesCount(fetchedPost.likes || 0);
          setIsLiked(fetchedPost.is_liked || false);
        } else {
          toastHelper.error("المنشور غير موجود");
        }
      } catch (error) {

        toastHelper.error("حدث خطأ أثناء تحميل المنشور");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  // Close share menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderTextWithLinks = (text) => {
    if (!text) return text;

    // Regex to match URLs (http, https, www, etc.)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      // Check if part is a URL
      if (urlRegex.test(part)) {
        // Reset the regex lastIndex for next test
        urlRegex.lastIndex = 0;
        let url = part;
        // Add https:// if it starts with www
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        }
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#7c3aed',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {part}
          </a>
        );
      }
      // Reset for next iteration
      urlRegex.lastIndex = 0;
      return part;
    });
  };



  const handleShare = (platform) => {
    if (!post) return;
    const url = window.location.origin + `/blog-viewer/${post.id}`;
    const text = post.title || htmlHelper.stripHtml(post.description) || "تحقق من هذا المنشور";
    let shareUrl = "";

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + " " + url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    window.open(shareUrl, '_blank');
    setShowShareMenu(false);
  };

  const handleLike = async () => {
    const userId = storageHelper.getItem("user_id");
    if (!userId) {
      toastHelper.error("يجب عليك تسجيل الدخول اولا");
      return;
    }

    const previousLiked = isLiked;
    const previousLikesCount = likesCount;
    const newIsLiked = !isLiked;
    const newLikesCount = newIsLiked ? Number(likesCount) + 1 : Number(likesCount) - 1;

    setIsLiked(newIsLiked);
    setLikesCount(newLikesCount);

    try {
      const response = await ApiServices.put(`Blog/Like/${post.id}`, {});
      if (!(response.check || response.status === 200)) {
        setIsLiked(previousLiked);
        setLikesCount(previousLikesCount);
      }
    } catch (error) {

      setIsLiked(previousLiked);
      setLikesCount(previousLikesCount);
    }
  };

  // ── Loading State ──
  if (loading) {
    return (
      <>
        <Header />
        <div className="blog-viewer-container">
          <div className="content-wrapper" style={{ textAlign: 'center', paddingTop: '80px' }}>
            <MiniLoader variant="dots" color="#7a6ad8" size={40} />
            <p style={{ marginTop: '20px', color: '#666', fontFamily: 'Zain, sans-serif' }}>
              جاري تحميل المنشور...
            </p>
          </div>
        </div>
      </>
    );
  }

  // ── Not Found State ──
  if (!post) {
    return (
      <>
        <Header />
        <div className="blog-viewer-container">
          <div className="content-wrapper" style={{ textAlign: 'center', paddingTop: '80px' }}>
            <p style={{ color: '#666', fontFamily: 'Zain, sans-serif', fontSize: '18px' }}>
              لم يتم العثور على المنشور.
            </p>
            <button className="back-button" onClick={() => navigate("/blogs")} style={{ margin: '20px auto', display: 'inline-flex' }}>
              <FontAwesomeIcon icon={faArrowRight} />
              <span>العودة للمدونة</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  const isHtml = post.content && /<[a-z][\s\S]*>/i.test(post.content);

  return (
    <>
      <Header />

      <div className="blog-viewer-container">
        <div className="content-wrapper">

          {/* Back Button */}
          <button className="back-button" onClick={() => navigate("/blogs")}>
            <FontAwesomeIcon icon={faArrowRight} />
            <span>العودة للمدونة</span>
          </button>

          {/* Main Post Card */}
          <div className="post-card">

            {/* Post Hero Image */}
            {post.image && (
              <div className="post-hero-image">
                <img
                  src={getFullFileUrl(post.image)}
                  alt={post.title}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            {/* Post Metadata Header */}
            <div className="post-meta-header">
              {/* Category Badge */}
              {post.category && (
                <div className="category-badge">
                  <FontAwesomeIcon icon={faTag} />
                  <span>{post.category}</span>
                </div>
              )}

              {/* Post Title */}
              <h1 className="post-main-title">{post.title}</h1>

              {/* Author + Date + Views Row */}
              <div className="post-author-row">
                <div className="author-info">
                  <div className="avatar-placeholder">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div className="meta-text">
                    <div className="author-name-wrapper">
                      <h3 className="author-name">
                        {post.is_platform_blog ? "منصة امتحاني" : (post.author || "مستخدم مجهول")}
                      </h3>
                      {post.is_platform_blog && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="verification-badge"
                          title="حساب موثق"
                        />
                      )}
                    </div>
                    <div className="sub-meta">
                      {post.date && (
                        <>
                          <FontAwesomeIcon icon={faCalendarAlt} className="meta-icon" />
                          <span>{formatDate(post.date)}</span>
                          <span className="dot-separator">•</span>
                        </>
                      )}
                      <FontAwesomeIcon icon={faEye} className="meta-icon" />
                      <span>{post.views || 0} مشاهدة</span>
                      <span className="dot-separator">•</span>
                      <FontAwesomeIcon icon={faGlobeAmericas} className="privacy-icon" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description Callout */}
            {post.description && (
              <div className="post-description-block">
                <p>{htmlHelper.stripHtml(post.description)}</p>
              </div>
            )}

            {/* Main Content */}
            <div className="post-content">
              {isHtml ? (
                <div
                  className="html-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              ) : (
                <div className="html-content text-content-expanded" style={{ whiteSpace: 'pre-wrap' }}>
                  {renderTextWithLinks(post.content || htmlHelper.stripHtml(post.description))}
                </div>
              )}
            </div>

            {/* Stats / Engagement Bar */}
            <div className="post-stats">
              <div className="stats-left">
                <span className="stat-icon-group">
                  <div className="icon-circle bg-blue">
                    <FontAwesomeIcon icon={faThumbsUp} />
                  </div>
                </span>
                <span className="stats-text">{likesCount} إعجاب</span>
              </div>
              <div className="stats-right">
                <span
                  className="stats-text clickable"
                  onClick={() => setShowComments(!showComments)}
                >
                  {post.comments_count || 0} تعليقات
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="post-actions" ref={shareMenuRef}>
              <button
                className={`action-button ${isLiked ? 'active' : ''}`}
                onClick={handleLike}
              >
                <FontAwesomeIcon icon={faThumbsUp} />
                <span>إعجاب</span>
              </button>

              <button
                className={`action-button ${showComments ? 'active' : ''}`}
                onClick={() => setShowComments(!showComments)}
              >
                <FontAwesomeIcon icon={faComment} />
                <span>تعليق</span>
              </button>

              <div className="share-wrapper" style={{ flex: 1, position: 'relative' }}>
                <button
                  className="action-button"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  style={{ width: '100%' }}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span>إرسال</span>
                </button>

                {showShareMenu && (
                  <div className="share-popup">
                    <button onClick={() => handleShare('facebook')} className="share-item">
                      <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2' }} /> فيسبوك
                    </button>
                    <button onClick={() => handleShare('whatsapp')} className="share-item">
                      <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25D366' }} /> واتساب
                    </button>
                    <button onClick={() => handleShare('linkedin')} className="share-item">
                      <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0A66C2' }} /> لينكد إن
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Comments Section */}
            {showComments && (
              <div className="post-comments-wrapper">
                <BlogComment blogId={post.id} postOwnerId={post.user_id} />
              </div>
            )}
          </div>

          {/* Related Blogs */}
          {relatedBlogs.length > 0 && (
            <div className="related-section">
              <h2 className="related-title">مقالات ذات صلة</h2>
              <div className="related-grid">
                {relatedBlogs.map((blog) => (
                  <div
                    key={blog.id}
                    className="related-card"
                    onClick={() => navigate(`/blog-viewer/${blog.id}`)}
                  >
                    {blog.image && (
                      <div className="related-card-img">
                        <img
                          src={getFullFileUrl(blog.image)}
                          alt={blog.title}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="related-card-body">
                      {blog.category && (
                        <span className="related-category">{blog.category}</span>
                      )}
                      <h4 className="related-card-title">{blog.title}</h4>
                      <p className="related-card-desc">{htmlHelper.stripHtml(blog.description)}</p>
                      <div className="related-card-meta">
                        <span>
                          <FontAwesomeIcon icon={faEye} /> {blog.views || 0}
                        </span>
                        <span>
                          <FontAwesomeIcon icon={faThumbsUp} /> {blog.likes || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      <style jsx>{`
        .blog-viewer-container {
          background-color: #f3f2ef;
          min-height: 100vh;
          padding-top: 200px;
          padding-bottom: 60px;
          direction: rtl;
            font-family: "Zain", sans-serif;
        }

        .content-wrapper {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 16px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 10px 16px;
          color: #555;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 20px;
          transition: all 0.2s;
            font-family: "Zain", sans-serif;
        }

        .back-button:hover {
          background: #f8f9fa;
          color: #7a6ad8;
          border-color: #7a6ad8;
        }

        /* ── Post Card ── */
        .post-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
          overflow: hidden;
          margin-bottom: 32px;
        }

        /* Hero Image */
        .post-hero-image {
          width: 100%;
          max-height: 460px;
          overflow: hidden;
          background: #eef3f8;
        }

        .post-hero-image img {
          width: 100%;
          height: 100%;
          max-height: 460px;
          object-fit: cover;
          display: block;
        }

        /* Meta Header */
        .post-meta-header {
          padding: 28px 28px 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f3f0ff;
          color: #7c3aed;
          border: 1px solid #ddd6fe;
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 14px;
        }

        .post-main-title {
          font-size: 26px;
          font-weight: 900;
          color: #1a1b4b;
          line-height: 1.5;
          margin: 0 0 18px 0;
        }

        .post-author-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .author-info {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .avatar-placeholder {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7c3aed, #a78bfa);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 20px;
          flex-shrink: 0;
          overflow: hidden;
        }

        .avatar-placeholder img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .meta-text {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }

        .author-name-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .author-name {
          font-weight: 700;
          font-size: 15px;
          color: #1e293b;
          margin: 0;
        }

        .verification-badge {
          color: #0a66c2;
          font-size: 14px;
          flex-shrink: 0;
        }

        .sub-meta {
          font-size: 13px;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .meta-icon {
          font-size: 11px;
          color: #94a3b8;
        }

        .privacy-icon {
          font-size: 11px;
        }

        .dot-separator {
          font-weight: bold;
          margin: 0 2px;
          color: #cbd5e1;
        }

        /* Description Callout */
        .post-description-block {
          margin: 0;
          padding: 18px 28px;
          background: #f8fafc;
          border-right: 4px solid #7c3aed;
        }

        .post-description-block p {
          margin: 0;
          font-size: 15px;
          color: #475569;
          line-height: 1.8;
          font-style: italic;
        }

        /* Post Content */
        .post-content {
          padding: 24px 28px;
          font-size: 16px;
          color: #1e293b;
          line-height: 1.8;
        }

        .html-content {
          font-size: 16px;
          line-height: 1.9;
          overflow-wrap: break-word;
          color: #334155;
        }

        .html-content h1, .html-content h2, .html-content h3, .html-content h4, .html-content h5 {
          color: #1a1b4b;
          margin-top: 28px;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .html-content h1 { font-size: 24px; }
        .html-content h2 { font-size: 22px; }
        .html-content h3 { font-size: 20px; }
        .html-content h4 { font-size: 18px; }

        .html-content p {
          margin-bottom: 14px;
        }

        .html-content ul, .html-content ol {
          padding-right: 24px;
          margin-bottom: 16px;
        }

        .html-content li {
          margin-bottom: 8px;
        }

        .html-content hr {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 24px 0;
        }

        .html-content img {
          max-width: 100%;
          height: auto;
          margin: 16px 0;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .html-content strong, .html-content b {
          color: #1e293b;
          font-weight: 800;
        }

        .html-content a {
          color: #7c3aed;
          text-decoration: none;
        }

        .html-content a:hover {
          text-decoration: underline;
        }

        /* Stats Bar */
        .post-stats {
          padding: 14px 24px;
          border-top: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14px;
          color: #666;
        }

        .stats-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stats-right {
          display: flex;
          gap: 8px;
        }

        .stats-text {
          font-size: 14px;
          color: #64748b;
        }

        .stat-icon-group {
          display: flex;
          align-items: center;
        }

        .icon-circle {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          color: white;
          border: 2px solid white;
        }

        .bg-blue { background-color: #0a66c2; }

        .clickable {
          cursor: pointer;
        }

        .clickable:hover {
          color: #0a66c2;
          text-decoration: underline;
        }

        /* Action Buttons */
        .post-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .action-button {
          background: none;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 10px;
          color: #64748b;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          border-radius: 6px;
          flex: 1;
          justify-content: center;
          transition: background 0.2s;
            font-family: "Zain", sans-serif;
        }

        .action-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
          color: #7c3aed;
        }

        .action-button.active,
        .action-button.active svg,
        .action-button.active span {
          color: #7c3aed !important;
          background-color: transparent;
        }

        /* Share Popup */
        .share-popup {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          border-radius: 10px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
          padding: 8px 0;
          z-index: 100;
          min-width: 160px;
          margin-bottom: 8px;
        }

        .share-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          font-size: 14px;
          color: #333;
          cursor: pointer;
          text-align: right;
          transition: background 0.2s;
            font-family: "Zain", sans-serif;
        }

        .share-item:hover {
          background-color: #f3f2ef;
        }

        .share-popup::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 6px;
          border-style: solid;
          border-color: white transparent transparent transparent;
        }

        /* Comments Section */
        .post-comments-wrapper {
          border-top: 1px solid #eee;
          padding: 24px 28px;
          background: #f9fafb;
        }

        /* ── Related Blogs ── */
        .related-section {
          padding: 8px 0 0;
        }

        .related-title {
          font-size: 20px;
          font-weight: 800;
          color: #1a1b4b;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e2e8f0;
        }

        .related-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 16px;
        }

        .related-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .related-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 28px rgba(124, 58, 237, 0.15);
          border-color: #ddd6fe;
        }

        .related-card-img {
          width: 100%;
          height: 140px;
          overflow: hidden;
          background: #eef3f8;
        }

        .related-card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s ease;
        }

        .related-card:hover .related-card-img img {
          transform: scale(1.05);
        }

        .related-card-body {
          padding: 14px;
        }

        .related-category {
          display: inline-block;
          background: #f3f0ff;
          color: #7c3aed;
          font-size: 11px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 20px;
          margin-bottom: 8px;
        }

        .related-card-title {
          font-size: 14px;
          font-weight: 800;
          color: #1e293b;
          margin: 0 0 8px 0;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .related-card-desc {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 10px 0;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .related-card-meta {
          display: flex;
          gap: 14px;
          font-size: 12px;
          color: #94a3b8;
          font-weight: 600;
        }

        .related-card-meta span {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .blog-viewer-container {
            padding-top: 100px;
          }

          .content-wrapper {
            padding: 0;
          }

          .back-button {
            margin: 0 16px 16px;
          }

          .post-card {
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .post-meta-header {
            padding: 20px 16px 14px;
          }

          .post-main-title {
            font-size: 20px;
          }

          .post-description-block {
            padding: 14px 16px;
          }

          .post-content {
            padding: 18px 16px;
            font-size: 15px;
          }

          .post-stats {
            padding: 12px 16px;
          }

          .post-actions {
            padding: 6px 12px;
          }

          .action-button {
            font-size: 13px;
            padding: 10px 6px;
          }

          .action-button span {
            display: none;
          }

          .post-comments-wrapper {
            padding: 16px;
          }

          .related-section {
            padding: 16px;
          }

          .related-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default BlogViewer;
